import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import useFormItemStatus from "antd/es/form/hooks/useFormItemStatus";
import { TextAreaProps } from "antd/es/input/TextArea";
import cn from "classnames";
import { BaseEditor, createEditor, Descendant, Editor, Range, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import type { ReactEditor } from "slate-react";
import { Editable, Slate, withReact } from "slate-react";
import { EditableProps } from "slate-react/dist/components/editable";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { LARGE_TEXT_LENGTH_LIMIT } from "@mapmycustomers/shared/util/consts/textLengthLimits";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import { useConfigProvider } from "../../../ConfigProvider";
import resetEditor from "../../../utils/resetEditor";
import ErrorRow from "../../ErrorRow";
import Labeled, { LabeledProps } from "../../Labeled";
import convertToText from "../util/convertToText";
import parseText from "../util/parseText";

import Element from "./mention/Element";
import MentionsDropdown from "./mention/MentionsDropdown";
import IsMentionDisabled from "./mention/type/IsMentionDisabled";
import MentionType from "./mention/type/MentionType";
import convertToTextWithMentions from "./mention/util/convertToTextWithMentions";
import decorator from "./mention/util/decorator";
import displayElements from "./mention/util/displayElements";
import findMention from "./mention/util/findMention";
import getTextLength, { createCache } from "./mention/util/getTextLength";
import insertMention from "./mention/util/insertMention";
import isValueEmpty from "./mention/util/isValueEmpty";
import parseTextWithMentions from "./mention/util/parseTextWithMentions";
import withMentions from "./mention/util/withMentions";
import withSingleLine from "./mention/util/withSingleLineText";

const getUserFilter = (search: string) => {
  const searchString = search.toLowerCase().trim();
  return ({ fullName, username }: User) =>
    searchString.length === 0 ||
    (fullName ?? "").toLowerCase().includes(searchString) ||
    username.toLowerCase().includes(searchString);
};

const getTeamFilter = (search: string) => {
  const searchString = search.toLowerCase().trim();
  return ({ name }: Team) => !searchString || name.toLowerCase().includes(searchString);
};

// Not sure can we provide all text area props.
interface TextAreaWithMentionsProps
  extends
    Pick<
      TextAreaProps,
      "autoFocus" | "className" | "disabled" | "maxLength" | "placeholder" | "rows"
    >,
    Omit<LabeledProps, "children"> {
  allowedUserIds?: User["id"][];
  allUsers: User[];
  caption?: string;
  disableMentions?: boolean;
  emptyTextForWithSkippedUsers?: string;
  error?: ReactNode;
  hideDisabledUsers?: boolean;
  isMentionDisabled?: IsMentionDisabled;
  // this prop can't be changed dynamically coz we use this during editor initialization, which can be performed only once
  isTextField?: boolean;
  locked?: boolean;
  onChange?: (text: string) => void;
  onModify?: (empty: boolean) => void;
  placeholder?: string;
  readOnly?: boolean;
  teams: Team[];
  value?: string;
}

const block = bem("mmc-text-area-with-mentions-field");

const TextAreaWithMentions = ({
  allowedUserIds,
  allUsers,
  caption,
  className,
  disableMentions = false,
  emptyTextForWithSkippedUsers,
  error,
  hideDisabledUsers,
  isMentionDisabled,
  isTextField,
  label,
  labelClassName,
  labelPosition,
  locked,
  maxLength = LARGE_TEXT_LENGTH_LIMIT,
  onChange,
  onModify,
  placeholder,
  readOnly,
  required,
  rowProps,
  sideLabelSpan,
  teams,
  value,
  ...props
}: TextAreaWithMentionsProps) => {
  const configProvider = useConfigProvider();
  const { status } = useFormItemStatus();

  const users = useMemo(() => {
    if (allowedUserIds) {
      const userIds = new Set(allowedUserIds);
      return allUsers.filter(({ id }) => userIds.has(id));
    } else {
      return allUsers;
    }
  }, [allowedUserIds, allUsers]);

  const [parsedValue, setParsedValue] = useState<Descendant[]>(
    disableMentions
      ? parseText(value ?? "")
      : parseTextWithMentions(value ?? "", users, teams, isMentionDisabled)
  );
  const lastValueRef = useRef<string | undefined>(value);
  const textLengthCache = useRef(createCache());

  const [target, setTarget] = useState<Range | undefined>();
  const [index, setIndex] = useState<number>(0);
  const [mentionType, setMentionType] = useState<MentionType>(MentionType.USER);

  const [search, setSearch] = useState("");
  const renderElement = useCallback<NonNullable<EditableProps["renderElement"]>>(
    (props) => <Element {...props} />,
    []
  );

  const textLength = useMemo(
    () => getTextLength(parsedValue, textLengthCache.current),
    [parsedValue]
  );

  const isTextLimitReached = useMemo(() => textLength >= maxLength, [maxLength, textLength]);

  const editorRef = useRef<ReactEditor & BaseEditor & HistoryEditor>();
  if (!editorRef.current) {
    let editor = withReact(createEditor());
    if (isTextField) {
      editor = withSingleLine(editor);
    }
    editor = withMentions(editor);
    editorRef.current = editor;
  }

  const editor = editorRef.current!;

  const userFilter = useMemo(() => getUserFilter(search), [search]);
  const teamFilter = useMemo(() => getTeamFilter(search), [search]);

  const filteredUsers = useMemo(() => {
    const preparedUsers =
      hideDisabledUsers && isMentionDisabled
        ? users.filter((user) => !isMentionDisabled(user))
        : users;
    return preparedUsers.filter(userFilter);
  }, [hideDisabledUsers, isMentionDisabled, userFilter, users]);

  const filteredTeams = useMemo(() => {
    return teams.filter(({ deletedAt }) => !deletedAt).filter(teamFilter);
  }, [teamFilter, teams]);

  const handleInsertMention = useCallback(
    (index: number, type: MentionType) => {
      if (target) {
        Transforms.select(editor, target);
      }
      insertMention(
        editor,
        filteredUsers[index - filteredTeams.length],
        filteredTeams[index],
        type,
        isMentionDisabled
      );
      setTarget(undefined);
    },
    [editor, filteredTeams, filteredUsers, isMentionDisabled, target]
  );

  const handlePaste = useCallback<NonNullable<EditableProps["onPaste"]>>(
    (e) => {
      const insertText: string | undefined = e?.clipboardData?.getData("Text");
      if (insertText) {
        if (textLength + insertText.length > maxLength) {
          e.preventDefault();
        }
      }
    },
    [maxLength, textLength]
  );

  const totalMentionOptions = filteredTeams.length + filteredUsers.length;

  const handleKeyDown = useCallback<NonNullable<EditableProps["onKeyDown"]>>(
    (event) => {
      if (target) {
        switch (event.key) {
          case "ArrowDown": {
            stopEvents(event);
            const prevIndex = index >= totalMentionOptions - 1 ? 0 : index + 1;
            setIndex(prevIndex);
            setMentionType(prevIndex < filteredTeams.length ? MentionType.TEAM : MentionType.USER);
            break;
          }
          case "ArrowUp": {
            stopEvents(event);
            const nextIndex = index <= 0 ? totalMentionOptions - 1 : index - 1;
            setIndex(nextIndex);
            setMentionType(nextIndex < filteredTeams.length ? MentionType.TEAM : MentionType.USER);
            break;
          }
          case "Tab":
          case "Enter":
            stopEvents(event);
            if (!disableMentions) {
              handleInsertMention(index, mentionType);
            }
            break;
          case "Escape":
            stopEvents(event);
            setTarget(undefined);
            break;
        }
      }
    },
    [
      target,
      disableMentions,
      index,
      totalMentionOptions,
      filteredTeams.length,
      handleInsertMention,
      mentionType,
    ]
  );

  const handleBlur = useCallback(() => {
    setParsedValue((currentValue) => {
      const newValue = disableMentions
        ? convertToText(currentValue)
        : convertToTextWithMentions(currentValue);
      if (lastValueRef.current !== newValue) {
        lastValueRef.current = newValue;
        onChange?.(newValue);
      }
      return currentValue;
    });
  }, [disableMentions, onChange]);

  useChangeTracking(() => {
    onModify?.(isValueEmpty(parsedValue));
  }, [onModify, parsedValue]);

  const notifyAboutChanges = useDebouncedCallback([
    (elements) => {
      const newValue = disableMentions
        ? convertToText(elements)
        : convertToTextWithMentions(elements);
      if (lastValueRef.current !== newValue) {
        lastValueRef.current = newValue;
        onChange?.(newValue);
      }
    },
    500,
  ]);

  const handleChange = useCallback(
    (elements: Descendant[]) => {
      notifyAboutChanges(elements);
      setParsedValue(elements);
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const mention = findMention(selection, editor);
        if (mention) {
          const { search, target } = mention;
          setTarget(target);
          setSearch(search);
          setIndex(0);
        } else {
          setTarget(undefined);
        }
      }
      const length = getTextLength(elements, textLengthCache.current);
      if (length > maxLength) {
        Transforms.delete(editor, {
          at: {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
          },
        });
        editor.insertText(
          (disableMentions
            ? convertToText(elements)
            : convertToTextWithMentions(elements)
          ).substring(0, maxLength)
        );
      }
    },
    [
      disableMentions,
      editor,
      maxLength,
      notifyAboutChanges,
      setIndex,
      setParsedValue,
      setSearch,
      setTarget,
    ]
  );

  const handleHover = useCallback((index: number, type: MentionType) => {
    setIndex(index);
    setMentionType(type);
  }, []);

  useEffect(() => {
    // Only set a new value when it is different, otherwise we're trying to avoid unnecessary re-rendering.
    if (lastValueRef.current !== value) {
      lastValueRef.current = value;
      const nodes = disableMentions
        ? parseText(value ?? "")
        : parseTextWithMentions(value ?? "", users, teams, isMentionDisabled);
      resetEditor(editorRef.current!);
      Transforms.insertNodes(editorRef.current!, nodes);
      setParsedValue(nodes);
    }
  }, [disableMentions, value, users, teams, isMentionDisabled]);

  const isDisabled = props.disabled || locked;

  const formattedError = useMemo(
    () =>
      maxLength && (value ?? "").length >= maxLength
        ? configProvider.formatMessage("ui.textField.error.limit")
        : error,
    [configProvider, error, maxLength, value]
  );

  return (
    <Labeled
      className={cn(block({ disabled: isDisabled, error: status === "error" }), className)}
      label={label}
      labelClassName={cn(block("label"), labelClassName)}
      labelPosition={labelPosition}
      required={required}
      rowProps={rowProps}
      sideLabelSpan={sideLabelSpan}
    >
      <Slate
        editor={editor}
        initialValue={parsedValue}
        onChange={isDisabled ? undefined : handleChange}
      >
        <Editable
          className={cn(block("editor", { readOnly, singleLineEditor: isTextField }), className)}
          decorate={decorator}
          disabled={isDisabled}
          onBlur={isDisabled ? undefined : handleBlur}
          onKeyDown={isDisabled ? undefined : handleKeyDown}
          onPaste={isDisabled ? undefined : handlePaste}
          placeholder={placeholder}
          readOnly={isDisabled || readOnly}
          renderElement={renderElement}
          renderLeaf={displayElements}
          {...props}
        />
        {!isDisabled && !disableMentions && target && (
          <MentionsDropdown
            activeIndex={index}
            editor={editor}
            emptyTextForWithSkippedUsers={
              totalMentionOptions > 0 ? emptyTextForWithSkippedUsers : undefined
            }
            onHover={handleHover}
            onSelect={handleInsertMention}
            target={target}
            teams={filteredTeams}
            users={filteredUsers}
          />
        )}
        {locked && <FontAwesomeIcon className={block("lock")} icon={faLock} />}
      </Slate>
      {isTextLimitReached ? (
        <ErrorRow>{formattedError}</ErrorRow>
      ) : caption ? (
        <div className={block("caption")}>{caption}</div>
      ) : null}
    </Labeled>
  );
};

export default TextAreaWithMentions;
